"use client";

import getDeviceType from "@/app/utils/device/getDeviceType";
import { getLastSearchRequestFromLocalStorage } from "@/app/utils/services/service/localStorageService";
import { Destination } from "@/types";
import axios from "axios";
import { useLocale, useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRightIcon,
  Cross,
  HotelIcon,
  MapIcon,
  MapMarkerDark,
  MapMarkerIcon,
} from "../Icons";
import { Modal } from "../modal";
import { setInputFocus } from "../utils/setInputFocus";
import { BedDouble, MapPin, MapPinned } from "lucide-react";
import { COLORS } from "@/app/common/consts/colors";
import { Place } from "@/app/types/common";

type ClearIconProps = {
  handleClear: () => void;
  color?: string;
};

// CloseIcon component
const ClearIcon = ({ handleClear, color }: ClearIconProps) => {
  return (
    <svg
      height="20"
      width="20"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
      fill={color ? color : "#ffffff"}
      onClick={handleClear}
    >
      <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
    </svg>
  );
};

type Props = {
  placeHolder: string;
  _options: Destination[];
  isSearchable: boolean;
  onOptionChange: (val: Destination) => void;
  selectedValue: Destination;
  setSelectedValue: (val: Destination) => void;
  isCustomClass?: boolean;
  isEventDetailPage?: boolean;
};

// CustomSelect component
const CustomSelect = ({
  placeHolder,
  _options,
  selectedValue,
  setSelectedValue,
  onOptionChange,
  isCustomClass,
  isEventDetailPage,
}: Props) => {
  const locale = useLocale();
  const t = useTranslations();

  const [showMenu, setShowMenu] = useState(false); // Controls the visibility of the dropdown menu
  const [searchValue, setSearchValue] = useState(""); // Stores the value entered in the search input
  const isMobile = getDeviceType().isMobileType;
  const [options, setOptions] = useState<Destination[]>([]);

  useEffect(() => {
    if (isEventDetailPage) return;
    const lastSearchRequest = getLastSearchRequestFromLocalStorage(locale);

    if (lastSearchRequest) {
      const { city, country, destination, id } = lastSearchRequest || {};
      setSearchValue(destination);
      setSelectedValue({
        city,
        country,
        destination,
        id,
      });
    }
  }, []);

  const searchRef = useRef<HTMLInputElement>(null); // Reference to the search input element
  const inputRef = useRef<HTMLDivElement>(null); // Reference to the custom select input element

  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (showMenu || searchRef.current) {
      if (options.length == 0) {
        setOptions([..._options.slice(0, 5)]);
      }
    }
    if (!showMenu) {
      !!selectedValue?.destination && setSearchValue(selectedValue.destination);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenu]);

  const fetchData = async (queryParams: string) => {
    try {
      const res = await axios.get<{ places: Place[] }>(
        `/api/places?query=${queryParams}&lang=${locale}`
      );

      const ops = res?.data?.places.slice(0, 5).map((place) => ({
        id: place.id,
        destination: place?.fullName,
        hotelCount: 0,
        country: "",
        city: place?.name,
        label: place?.fullName,
        value: place?.name,
      }));

      setOptions([...ops]);
    } catch (er) {}
  };

  useEffect(() => {
    let getData: any = "";
    if (searchValue != selectedValue?.destination) {
      getData = setTimeout(() => {
        fetchData(searchValue);
      }, 500);
    }

    return () => {
      getData && clearTimeout(getData);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    const handler = (e: any) => {
      if (inputRef.current && !inputRef?.current?.contains(e.target)) {
        if (!isMobile) {
          setShowMenu(false);
        }
      }
    };

    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
    };
  }, []);

  useEffect(() => {
    if (!!selectedValue?.destination) {
      setSearchValue(selectedValue.destination);
    }
  }, [selectedValue]);

  const handleInputClick = (e: any) => {
    e.preventDefault();
    setShowMenu(true);
    document.body.classList.add("no-scroll-input-modal");
  };

  useEffect(() => {
    if (showMenu) {
      const timer = setTimeout(() => {
        setInputFocus(searchRef);
      }, 0);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [showMenu]);

  const onItemClick = (option: Destination) => {
    const newValue = option;

    setSelectedValue({ ...newValue });
    setSearchValue(newValue?.destination ?? "");

    onOptionChange(option);

    if (isMobile) {
      setShowMenu(false);
    }
  };

  const onSearch = (e: any) => {
    const inputValue = e?.target?.value;
    if (!inputValue) {
      setOptions([..._options.slice(0, 5)]);
      setSelectedValue({});
      onOptionChange({});
    }
    setSearchValue(inputValue);

    if (!showMenu) {
      setShowMenu(true);
    }

    setSearchValue(inputValue);
  };

  const handleClear = () => {
    setSelectedValue({});
    setSearchValue("");
    onOptionChange({});
    setInputFocus(searchRef);
  };

  const renderOptions = (options: Destination[]) =>
    options.map((option, index) => formatDestination(option, index));

  const formatDestination = (option: Destination, index: number) => {
    const { destination, city, id } = option;

    return (
      <div
        className={`flex justify-between items-center w-full bg-white dark:bg-sleap-gray-950 pe-4 border-b border-sleap-gray-400 dark:border-sleap-gray-600  cursor-pointer hover:bg-sleap-purple-350 ${index === 0 ? "md:rounded-t-xl" : ""} ${index === options.length - 1 ? "md:rounded-b-xl" : ""}`}
        onClick={() => onItemClick(option)}
        key={id}
      >
        <div className={`flex p-2 gap-3 search-item md:items-center`}>
          <div className="mt-2 pl-2 ">
            {id?.charAt(0).toUpperCase() === "H" ? (
              <BedDouble
                className="w-5 h-5"
                color={
                  resolvedTheme === "dark"
                    ? COLORS["offWhite"]
                    : COLORS["lightblack"]
                }
              />
            ) : id?.charAt(0).toUpperCase() === "R" ? (
              <MapPinned
                className="w-5 h-5"
                color={resolvedTheme === "dark" ? "#FAFAFA" : "#09090b"}
              />
            ) : (
              <MapPin
                className="w-5 h-5"
                color={
                  resolvedTheme === "dark"
                    ? COLORS["offWhite"]
                    : COLORS["lightblack"]
                }
              />
            )}
          </div>
          <div className="w-full flex-grow flex flex-col gap-0 lg:gap-0.5 py-0.5 pl-3 items-start">
            <p className="text-base mb-0.5 font-semibold font-switzer text-sleap-gray-950 dark:text-lightwhite">
              {city}
            </p>
            {!!destination && (
              <div className="flex lg:justify-end lg:text-end lg:flex-wrap gap-0.5">
                <p className="text-xs mb-0.5 text-sleap-gray-950 dark:text-lightwhite opacity-50 text-left">
                  {destination}
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="p-2 bg-sleap-purple-300 rounded-full md:hidden block">
          <ArrowRightIcon color="white" />
        </div>
      </div>
    );
  };
  const renderClear = () => selectedValue?.destination || searchValue;

  return (
    <div className="sm:relative sm:0 location-modal-input-container">
      <div
        className={`location-input-container md:w-full h-12 flex flex-row rounded-full gap-2 pl-6 border-[1px] 2xl:border-2 dark:border-input border-sleap-gray-400 items-center ${isCustomClass ? "bg-transparent hover:border-sleap-gray-50" : "dark:bg-sleap-gray-950 bg-lightwhite hover:border-sleap-gray-600"}  dark:hover:border-white `}
      >
        <div className="dark:block hidden">
          <MapPin className="w-5 h-5" color="#fff" />
        </div>
        <div className="dark:hidden block">
          <MapPin
            className="w-5 h-5"
            color={isCustomClass ? "#fff" : "#14141E"}
          />
        </div>
        <div ref={inputRef} className="flex flex-1 items-center">
          <input
            ref={searchRef}
            onClick={handleInputClick}
            className={`${isCustomClass ? "bg-transparent text-white placeholder:opacity-50 placeholder:text-white" : "dark:bg-sleap-gray-950 bg-lightwhite text-sleap-gray-950 placeholder:text-sleap-gray-950"} w-full dark:text-white font-medium border-none outline-none text-ellipsis overflow-hidden placeholder:text-left placeholder:font-normal placeholder:opacity-50 dark:placeholder:text-sleap-gray-100  text-left`}
            placeholder={placeHolder}
            value={searchValue}
            onChange={onSearch}
            spellCheck={false}
            readOnly={showMenu && isMobile}
          />
          <div className="flex pr-6">
            {renderClear() && (
              <div>
                <span className="dark:block hidden">
                  <ClearIcon handleClear={handleClear} />
                </span>
                <span className="dark:hidden block">
                  <ClearIcon
                    handleClear={handleClear}
                    color={isCustomClass ? "#fff" : "#09090B"}
                  />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isMobile && showMenu && options.length > 0 && (
        <div
          className={`absolute left-0 z-50 w-[360px] sm:p-0.5 sm:pe-[2.25px] sm:rounded-xl sm:h-fit h-full `}
        >
          <div className="rounded-xl dark:text-lightwhite text-sleap-gray-950 bg-lightwhite dark:bg-sleap-gray-950 border border-sleap-gray-950">
            {renderOptions(options)}
          </div>
        </div>
      )}
      <Modal
        isOpen={showMenu && isMobile}
        width="w-full h-full overflow-hidden !rounded-none bg-white dark:bg-sleap-gray-950 !m-0"
        height="!p-0"
        outerClassName=""
        innerClassName="rounded-2xl p-0"
        bgColor="bg-white dark:bg-sleap-gray-950"
        isInputModal={true}
      >
        <div className="flex justify-between items-center py-4 bg-sleap-gray-950 px-4 mb-2">
          <h4 className="text-white font-bold font-unbounded text-lg mb-0 ">
            {t("HomePageHotels.destination")}
          </h4>
          <span
            className="p-2 rounded-full bg-sleap-gray-650 cursor-pointer"
            onClick={() => setShowMenu(false)}
          >
            <Cross color={"#fff"} />
          </span>{" "}
        </div>
        <div className="md:w-full h-12 flex flex-row rounded-full gap-0.5 border border-sleap-gray-400 items-center bg-white dark:bg-sleap-gray-950 px-6 mx-4">
          <div>
            <MapMarkerIcon
              color={resolvedTheme === "dark" ? "offWhite" : "lightblack"}
            />
          </div>
          <div
            // ref={inputRef}
            className="flex flex-1 items-center"
          >
            <input
              ref={searchRef}
              className="bg-white dark:bg-sleap-gray-950 w-full outline-none pl-2.5 pr-2 text-ellipsis overflow-hidden placeholder:text-sleap-gray-950 dark:placeholder:text-sleap-gray-100 text-sleap-gray-950 dark:text-white font-switzer text-base font-medium"
              placeholder={placeHolder}
              value={searchValue}
              onChange={onSearch}
              onClick={handleInputClick}
              spellCheck={false}
            />
            <div className="flex">
              {renderClear() && (
                <div>
                  <ClearIcon
                    color={resolvedTheme === "dark" ? "#fff" : "#09090B"}
                    handleClear={handleClear}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        {showMenu && options.length > 0 && (
          <div
            className={`absolute right-0 z-20 w-full sm:p-0.5 sm:pe-[2.25px] bg-white dark:bg-sleap-gray-950 sm:rounded-xl sm:h-fit h-full px-4`}
          >
            <div className="rounded-xl ">{renderOptions(options)}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomSelect;
